import React from "react";
import { ApiQuery } from "~/modules/filters/types";
import { AggregateQueryControls, VisualizationControlStates } from "./types";
import { VisualizationCard } from "../VisualizationCard";
import { StackedControls } from "./StackedControls";
import { GroupByControls } from "./GroupByControls";
import { FadeIn } from "~/components/FadeIn";
import { LogoLoadingSpinner } from "~/components/LoadingSpinner";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { ExportTableNames } from "~/modules/downloads/types";
import {
    VisualizationModeControls,
    VisualizationModeSelector,
} from "./VisualizationModeSelector";
import { AggregateFunctionControls } from "./AggregateFunctionControls";
import * as Analytics from "../../analytics";
import { DEFAULT_COL_SPANS } from "../VisualizationCard";
import { ColumnSpanConfig, DistributionType, DistributionTypes } from "~/types";
import { AggregateIntervalControls } from "./AggregateIntervalControls";
import { DistributionSelect } from "~/components/DistributionSelect";
import { TableToggle } from "~/components/TableToggle";
import { Separator } from "~/components/ui/separator";
import { TimeseriesSegmentSelectOption } from "./types";
import {
    DISTRIBUTION_SEGMENT_OPTIONS,
    MoreControlsPanel,
} from "./MoreControlsPanel";
import { AggregateFunctions } from "~/modules/filters/types";
import { cn } from "~/lib/utils";
import { VisualizationApiToolsButton } from "../VisualizationApiToolsButton";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { VisualizationDropdown } from "../VisualizationDropdown";
import { ButtonGroup } from "~/components/ButtonGroup";
import { faLock } from "@fortawesome/pro-solid-svg-icons";

// // // //

// ENHANCEMENT - add ESDoc header for this
// ENHANCEMENT - clean this up a lot, should be more customizable
export function TimeseriesCard(props: {
    title: string;
    subtitle?: string;
    apiQuery: ApiQuery;
    controls: VisualizationControlStates;
    modeControls: VisualizationModeControls;
    loading?: boolean;
    empty?: boolean;
    aggregateQueryControls: AggregateQueryControls;
    colSpans?: ColumnSpanConfig;
    children: React.ReactNode;
    height?: number;
    exportData?: {
        enabled: boolean;
        ignoreFeatureFlag?: boolean;
        filename?: string;
        exportTableName?:
            | ExportTableNames.asset_timeseries_v4
            | ExportTableNames.token_timeseries_v4
            | ExportTableNames.fund_flows_timeseries_v4;
    };
    apiTools?: {
        enabled: boolean;
        baseUrl: string;
    };
    customControls?: React.ReactNode;
    cardStyle?: React.CSSProperties;
    distributionSelect?: {
        value: DistributionType;
        onChange: (value: DistributionType) => void;
        enabledOptions: {
            distributed: boolean;
            represented: boolean;
        };
    };
    stablecoinsToggle?: {
        checked: boolean;
        onChange: (value: boolean) => void;
    };
    segmentSelect?: {
        label: string;
        options: TimeseriesSegmentSelectOption[];
        value: string;
        onChange: (value: string) => void;
    };
}) {
    const {
        title,
        subtitle,
        apiQuery,
        controls,
        aggregateQueryControls,
        loading = false,
        empty = false,
        customControls = null,
        colSpans = DEFAULT_COL_SPANS,
        modeControls,
        children,
        cardStyle,
        height = 300, // NOTE - this should be encapsulated elsewhere so it automatically applies to both the card and the visualization
        exportData = {
            enabled: false,
            ignoreFeatureFlag: false,
        },
        apiTools = {
            enabled: false,
            baseUrl: "",
        },
        distributionSelect,
        stablecoinsToggle,
        segmentSelect,
    } = props;

    const mode = modeControls.activeMode;

    // Get exportTableName for data downloads
    const exportTableName = exportData.exportTableName
        ? exportData.exportTableName
        : ExportTableNames.token_timeseries_v4;

    const exportFilename = exportData.filename ? exportData.filename : mode.key;

    const enableControls =
        controls.legend.enabled ||
        controls.navigator.enabled ||
        controls.stacked.enabled ||
        exportData.enabled ||
        apiTools.enabled;

    // WIP - the MoreControlsPanel disclosure is currently rendered
    // alongside the existing controls so both can be exercised side by side.
    const [moreControlsOpen, setMoreControlsOpen] = React.useState(false);

    // Change handlers shared between the standalone controls and the
    // MoreControlsPanel popover, so both fire the same analytics.
    const toggleStacked = () => {
        const newStacked = !controls.stacked.active;
        controls.stacked.toggle();

        // Track analytics event
        if (newStacked) {
            Analytics.trackShowPercentOfTotalChart({ visualization: mode.key });
        } else {
            Analytics.trackShowCumulativeChart({ visualization: mode.key });
        }
    };

    const changeAggregateFunction = (updatedValue: AggregateFunctions) => {
        // Track analytics event
        Analytics.trackChangeTimeseriesAggregateFunction({
            visualization: mode.key,
            prevValue: aggregateQueryControls.aggregateFunction.value,
            nextValue: updatedValue,
        });

        // Update aggregate query
        aggregateQueryControls.aggregateFunction.onChange(updatedValue);
    };

    const changeSegment = (updatedValue: string) => {
        if (!segmentSelect) return;

        // Track analytics event
        Analytics.trackChangeTimeseriesSegment({
            visualization: mode.key,
            segmentLabel: segmentSelect.label,
            prevValue: segmentSelect.value,
            nextValue: updatedValue,
        });

        segmentSelect.onChange(updatedValue);
    };

    // The distribution segments the page has enabled. DistributionSelect hides
    // itself below three options, so the panel's segment matches that rule.
    const distributionOptions = DISTRIBUTION_SEGMENT_OPTIONS.filter(
        (option) => {
            if (option.value === DistributionTypes.Distributed) {
                return !!distributionSelect?.enabledOptions.distributed;
            }
            if (option.value === DistributionTypes.Represented) {
                return !!distributionSelect?.enabledOptions.represented;
            }
            // "All" only appears when both of the others do
            return (
                !!distributionSelect?.enabledOptions.distributed &&
                !!distributionSelect?.enabledOptions.represented
            );
        },
    );

    // Defines whether the distribution select should be shown
    const showDistributionSelect =
        distributionSelect &&
        distributionSelect.enabledOptions.distributed &&
        distributionSelect.enabledOptions.represented;

    // Represented assets have no stablecoin breakdown, so the toggle reads as
    // off and does nothing in that mode — same rule as the standalone toggle.
    const stablecoinsDisabled =
        distributionSelect?.value === DistributionTypes.Represented;

    return (
        <DataExportProvider ignoreFeatureFlag={exportData.ignoreFeatureFlag}>
            {({ isLoggedIn, isLoading, exportCsv }) => (
                <VisualizationCard
                    customControls={
                        // This can be fully abstracted yeah?
                        enableControls ? (
                            <>
                                <div className="flex items-center gap-3">
                                    {apiTools.enabled && (
                                        <VisualizationApiToolsButton
                                            query={apiQuery}
                                            apiDocsBaseUrl={apiTools.baseUrl}
                                        />
                                    )}

                                    {exportData.enabled && (
                                        <DownloadButton
                                            testID="visualization-controls--download-button"
                                            disabled={isLoading}
                                            onClick={() => {
                                                exportCsv({
                                                    query: apiQuery,
                                                    exportTableName,
                                                    exportFilename,
                                                });
                                            }}
                                        />
                                    )}

                                    <VisualizationDropdown
                                        legend={
                                            controls.legend.enabled
                                                ? {
                                                      value: controls.legend
                                                          .active,
                                                      toggle: () => {
                                                          const newShowLegend =
                                                              !controls.legend
                                                                  .active;
                                                          controls.legend.toggle();

                                                          // Track analytics event
                                                          if (newShowLegend) {
                                                              Analytics.trackShowChartLegend(
                                                                  {
                                                                      visualization:
                                                                          mode.key,
                                                                  },
                                                              );
                                                          } else {
                                                              Analytics.trackHideChartLegend(
                                                                  {
                                                                      visualization:
                                                                          mode.key,
                                                                  },
                                                              );
                                                          }
                                                      },
                                                  }
                                                : undefined
                                        }
                                        navigator={
                                            controls.navigator.enabled
                                                ? {
                                                      value: controls.navigator
                                                          .active,
                                                      onChange: () => {
                                                          const newShowNavigator =
                                                              !controls
                                                                  .navigator
                                                                  .active;
                                                          controls.navigator.toggle();

                                                          // Track analytics event
                                                          if (
                                                              newShowNavigator
                                                          ) {
                                                              Analytics.trackShowChartTimeframeNavigator(
                                                                  {
                                                                      visualization:
                                                                          mode.key,
                                                                  },
                                                              );
                                                          } else {
                                                              Analytics.trackHideChartTimeframeNavigator(
                                                                  {
                                                                      visualization:
                                                                          mode.key,
                                                                  },
                                                              );
                                                          }
                                                      },
                                                  }
                                                : undefined
                                        }
                                    />
                                </div>
                            </>
                        ) : // NOTE - this logic sucks
                        // What we're doing here:
                        // If `enableControls` is false, BUT there are custom controls -> we render
                        // the custom controls in the location of the standard control menu
                        customControls ? (
                            customControls
                        ) : null
                    }
                    customControlBar={
                        // ENHANCEMENT - pass null here if there are no controls to display
                        // i.e. only one mode, no groupby/agg.function controls
                        // `@container` scopes the `@*:` variants below (and in
                        // VisualizationModeSelector) to the width of the card
                        // rather than the viewport. These cards render at a
                        // range of column spans — full width on one page, half
                        // on another — so a viewport breakpoint reports
                        // "desktop" for cards that are nowhere near wide enough
                        // to lay their controls out in a row.
                        <div className="@container flex flex-col items-start mb-2">
                            <div className="flex flex-col @4xl:flex-row items-start @4xl:items-center justify-start gap-3 @4xl:gap-6 mb-2 mt-1 w-full">
                                {showDistributionSelect && (
                                    <>
                                        {/* Below `@2xl` this moves into the MoreControlsPanel popover */}
                                        <div className="hidden @2xl:block">
                                            <DistributionSelect
                                                value={distributionSelect.value}
                                                includeDescription={false}
                                                includeTooltips={true}
                                                disabled={loading}
                                                enabledOptions={
                                                    distributionSelect.enabledOptions
                                                }
                                                onChange={
                                                    distributionSelect.onChange
                                                }
                                            />
                                        </div>
                                        {distributionSelect.enabledOptions
                                            .distributed &&
                                            distributionSelect.enabledOptions
                                                .represented && (
                                                <Separator
                                                    orientation="vertical"
                                                    className="hidden @4xl:block data-[orientation=vertical]:h-10"
                                                />
                                            )}
                                    </>
                                )}

                                <VisualizationModeSelector
                                    modeControls={modeControls}
                                />

                                {segmentSelect && (
                                    <>
                                        <Separator
                                            orientation="vertical"
                                            className="hidden @4xl:block data-[orientation=vertical]:h-10"
                                        />
                                        {/* Below `@2xl` this moves into the
                                            MoreControlsPanel popover */}
                                        <div className="hidden @2xl:block">
                                            <ButtonGroup
                                                title={segmentSelect.label}
                                                options={segmentSelect.options.map(
                                                    (option) => ({
                                                        label: option.label,
                                                        value: option.value,
                                                        tooltip: option.tooltip,
                                                        disabled:
                                                            option.loginRequired &&
                                                            !isLoggedIn,
                                                        icon:
                                                            option.loginRequired &&
                                                            !isLoggedIn
                                                                ? faLock
                                                                : undefined,
                                                        iconClassName: "h-2.5",
                                                    }),
                                                )}
                                                value={segmentSelect.value}
                                                disabled={loading}
                                                onValueChange={changeSegment}
                                            />
                                        </div>
                                    </>
                                )}

                                {stablecoinsToggle && (
                                    <>
                                        <Separator
                                            orientation="vertical"
                                            className="hidden @4xl:block data-[orientation=vertical]:h-10"
                                        />
                                        {/* Below `@2xl` this moves into the
                                            MoreControlsPanel popover */}
                                        <div className="hidden @2xl:block">
                                            <TableToggle
                                                checked={
                                                    distributionSelect?.value ===
                                                    DistributionTypes.Represented
                                                        ? false
                                                        : stablecoinsToggle.checked
                                                }
                                                onChange={
                                                    stablecoinsToggle.onChange
                                                }
                                                label="Include Stablecoins"
                                                description="Include stablecoins, cash and cash-equivalents"
                                                disabled={
                                                    distributionSelect?.value ===
                                                    DistributionTypes.Represented
                                                }
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex flex-col gap-2 items-start w-full @2xl:flex-row @2xl:gap-4 @2xl:items-center mb-2 @2xl:mb-0">
                                {/* Below `@2xl` these two sit side by side at
                                    half width each, with their labels stacked
                                    above them — or full width when only one of
                                    them is enabled. `contents` drops the grid
                                    at `@2xl` so they rejoin the row as usual. */}
                                <div
                                    className={cn(
                                        "grid w-full gap-2 @2xl:contents",
                                        {
                                            "grid-cols-2":
                                                aggregateQueryControls.groupBy
                                                    .enabled &&
                                                aggregateQueryControls.interval
                                                    .enabled,
                                            "grid-cols-1":
                                                !aggregateQueryControls.groupBy
                                                    .enabled ||
                                                !aggregateQueryControls.interval
                                                    .enabled,
                                        },
                                    )}
                                >
                                    {aggregateQueryControls.groupBy.enabled && (
                                        <GroupByControls
                                            stackLabelOnNarrow
                                            value={
                                                aggregateQueryControls.groupBy
                                                    .value as any
                                            }
                                            allowedOptions={
                                                aggregateQueryControls.groupBy
                                                    .allowedOptions as any
                                            }
                                            labelOverrides={
                                                aggregateQueryControls.groupBy
                                                    .labelOverrides as any
                                            }
                                            optionsWithPingIndicator={
                                                aggregateQueryControls.groupBy
                                                    .optionsWithNewIndicator as any
                                            }
                                            onChange={(updatedValue) => {
                                                // Track analytics event
                                                Analytics.trackChangeTimeseriesGroupBy(
                                                    {
                                                        visualization: mode.key,
                                                        prevValue:
                                                            aggregateQueryControls
                                                                .groupBy.value,
                                                        nextValue: updatedValue,
                                                    },
                                                );

                                                // Update aggregate query
                                                aggregateQueryControls.groupBy.onChange(
                                                    updatedValue as any,
                                                );
                                            }}
                                        />
                                    )}

                                    {aggregateQueryControls.interval
                                        .enabled && (
                                        <AggregateIntervalControls
                                            stackLabelOnNarrow
                                            value={
                                                aggregateQueryControls.interval
                                                    .value
                                            }
                                            allowedOptions={
                                                aggregateQueryControls.interval
                                                    .allowedOptions
                                            }
                                            onChange={(updatedValue) => {
                                                // Track analytics event
                                                Analytics.trackChangeTimeseriesInterval(
                                                    {
                                                        visualization: mode.key,
                                                        prevValue:
                                                            aggregateQueryControls
                                                                .interval.value,
                                                        nextValue: updatedValue,
                                                    },
                                                );

                                                // Update aggregate query
                                                aggregateQueryControls.interval.onChange(
                                                    updatedValue,
                                                );
                                            }}
                                        />
                                    )}
                                </div>

                                {aggregateQueryControls.aggregateFunction
                                    .enabled && (
                                    <div className="hidden @2xl:block">
                                        <AggregateFunctionControls
                                            value={
                                                aggregateQueryControls
                                                    .aggregateFunction.value
                                            }
                                            allowedOptions={
                                                aggregateQueryControls
                                                    .aggregateFunction
                                                    .allowedOptions
                                            }
                                            onChange={changeAggregateFunction}
                                        />
                                    </div>
                                )}

                                {controls.stacked.enabled && (
                                    // Below `@2xl` this moves into the
                                    // MoreControlsPanel popover
                                    <div className="hidden @2xl:block">
                                        <StackedControls
                                            value={controls.stacked.active}
                                            onToggle={toggleStacked}
                                        />
                                    </div>
                                )}

                                {/* NOTE - THIS PART IS A CUSTOM CONTROL */}
                                {/* PERHAPS IT CAN BE HANDLED BY "MODE SELECTION"? */}
                                {/* Render props.customControls here IFF enableControls is TRUE */}
                                {enableControls && <>{customControls}</>}
                            </div>

                            {/* The mobile "More Controls" popover. Below
                                `@2xl` it takes over for the controls that hide
                                themselves at that width — the distribution
                                select and the "Show % of Total" toggle. */}
                            <div className="w-full @2xl:hidden">
                                <MoreControlsPanel
                                    isLoggedIn={isLoggedIn}
                                    open={moreControlsOpen}
                                    onOpenChange={setMoreControlsOpen}
                                    disabled={loading}
                                    distribution={
                                        distributionSelect && {
                                            enabled:
                                                distributionOptions.length >= 3,
                                            value: distributionSelect.value,
                                            options: distributionOptions,
                                            onChange:
                                                distributionSelect.onChange,
                                        }
                                    }
                                    segment={
                                        segmentSelect && {
                                            enabled: true,
                                            label: segmentSelect.label,
                                            value: segmentSelect.value,
                                            options: segmentSelect.options.map(
                                                (option) => ({
                                                    label: option.label,
                                                    value: option.value,
                                                    tooltip: option.tooltip,
                                                    loginRequired:
                                                        option.loginRequired,
                                                }),
                                            ),
                                            onChange: changeSegment,
                                        }
                                    }
                                    aggregateFunction={{
                                        enabled:
                                            aggregateQueryControls
                                                .aggregateFunction.enabled,
                                        value: aggregateQueryControls
                                            .aggregateFunction.value,
                                        allowedOptions:
                                            aggregateQueryControls
                                                .aggregateFunction
                                                .allowedOptions,
                                        onChange: changeAggregateFunction,
                                    }}
                                    scale={{
                                        enabled: controls.stacked.enabled,
                                        value: controls.stacked.active,
                                        // Charts open unstacked
                                        defaultValue: false,
                                        onChange: toggleStacked,
                                    }}
                                    stablecoins={
                                        stablecoinsToggle && {
                                            enabled: true,
                                            value: stablecoinsDisabled
                                                ? false
                                                : stablecoinsToggle.checked,
                                            onChange:
                                                stablecoinsToggle.onChange,
                                        }
                                    }
                                />
                            </div>
                        </div>
                    }
                    colSpans={colSpans}
                    title={title}
                    subtitle={subtitle}
                    wrapperClassNameOverride=""
                    watermark={loading || empty ? "none" : "center-30pct"}
                    showTimestamp={false}
                    cardStyle={cardStyle}
                >
                    <>
                        {loading && (
                            <FadeIn>
                                <div
                                    className="flex items-center justify-center w-full"
                                    style={{
                                        height: `${height}px`,
                                    }}
                                >
                                    <LogoLoadingSpinner size={75} />
                                </div>
                            </FadeIn>
                        )}

                        {!loading && children}
                    </>
                </VisualizationCard>
            )}
        </DataExportProvider>
    );
}
